import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios'
import toast from 'react-hot-toast'

// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'

// Create axios instance
export const api: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Token management
const TOKEN_KEY = 'bid_checker_token'
const REFRESH_TOKEN_KEY = 'bid_checker_refresh_token'

export const tokenStorage = {
  getToken: (): string | null => localStorage.getItem(TOKEN_KEY),
  setToken: (token: string): void => localStorage.setItem(TOKEN_KEY, token),
  removeToken: (): void => localStorage.removeItem(TOKEN_KEY),
  getRefreshToken: (): string | null => localStorage.getItem(REFRESH_TOKEN_KEY),
  setRefreshToken: (token: string): void => localStorage.setItem(REFRESH_TOKEN_KEY, token),
  removeRefreshToken: (): void => localStorage.removeItem(REFRESH_TOKEN_KEY),
  clear: (): void => {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
  },
}

// Note: Request and response interceptors are now handled by the auth interceptor
// in lib/auth/authInterceptor.ts which is set up by the IframeAuthProvider

// API Response Types
export interface ApiResponse<T = any> {
  data: T
  message?: string
  status: number
}

export interface PaginatedResponse<T> {
  data: T[]
  total_count: number
  page: number
  page_size: number
  total_pages: number
}

export interface ApiError {
  error: string
  message: string
  details?: any
}

// Generic API functions
export const apiClient = {
  get: <T = any>(url: string, params?: any): Promise<T> =>
    api.get(url, { params }).then((response) => response.data),

  post: <T = any>(url: string, data?: any): Promise<T> =>
    api.post(url, data).then((response) => response.data),

  put: <T = any>(url: string, data?: any): Promise<T> =>
    api.put(url, data).then((response) => response.data),

  patch: <T = any>(url: string, data?: any): Promise<T> =>
    api.patch(url, data).then((response) => response.data),

  delete: <T = any>(url: string): Promise<T> =>
    api.delete(url).then((response) => response.data),

  upload: <T = any>(url: string, formData: FormData, onUploadProgress?: (progress: number) => void): Promise<T> =>
    api.post(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (progressEvent) => {
        if (onUploadProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          onUploadProgress(progress)
        }
      },
    }).then((response) => response.data),

  download: (url: string, filename?: string): Promise<void> =>
    api.get(url, { responseType: 'blob' }).then((response) => {
      const url_obj = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url_obj
      link.setAttribute('download', filename || 'download')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url_obj)
    }),
}

// Authentication API
export interface LoginRequest {
  username: string
  password: string
}

export interface LoginResponse {
  access_token: string
  refresh_token: string
  token_type: string
  user: User
}

export interface User {
  id: string
  username: string
  email: string
  full_name: string
  roles: string[]
  is_active: boolean
  is_verified: boolean
  created_at: string
  last_login: string
}

export const authApi = {
  login: (credentials: LoginRequest): Promise<LoginResponse> => {
    // Convert to form data for OAuth2 password flow
    const params = new URLSearchParams()
    params.append('username', credentials.username)
    params.append('password', credentials.password)
    
    return api.post('/auth/login', params, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    }).then((response) => response.data)
  },

  logout: (): Promise<void> =>
    apiClient.post('/auth/logout'),

  refreshToken: (refresh_token: string): Promise<LoginResponse> =>
    apiClient.post('/auth/refresh', { refresh_token }),

  getCurrentUser: (): Promise<User> =>
    apiClient.get('/auth/me'),

  updateProfile: (data: Partial<User>): Promise<User> =>
    apiClient.put('/auth/profile', data),

  changePassword: (data: { current_password: string; new_password: string }): Promise<void> =>
    apiClient.post('/auth/change-password', data),
}

// Projects API
export interface Project {
  id: string
  title: string
  description: string
  project_number: string
  rfp_file_path: string
  rfp_file_name: string
  rfp_file_size: number
  publish_date: string
  due_date: string
  status: 'draft' | 'in_progress' | 'completed' | 'closed'
  extraction_status: 'pending' | 'in_progress' | 'completed' | 'failed'
  extraction_error?: string
  auto_evaluate: boolean
  notification_emails: string[]
  vendor_count: number
  total_requirements: number
  vendor_responses?: VendorResponse[]
  requirements?: Requirement[]
  created_at: string
  created_by: User
  last_modified_by?: User
}

export interface ProjectCreateRequest {
  title: string
  description: string
  project_number?: string
  rfp_file_path: string
  publish_date?: string
  due_date?: string
  auto_evaluate?: boolean
  notification_emails?: string[]
  cover_letter_only?: boolean
}

export interface ProjectSearchRequest {
  query?: string
  status?: string
  created_by?: string
  extraction_status?: string
  date_from?: string
  date_to?: string
  due_date_from?: string
  due_date_to?: string
  is_past_due?: boolean
}

export const projectsApi = {
  list: (params?: ProjectSearchRequest & { skip?: number; limit?: number }): Promise<PaginatedResponse<Project>> =>
    apiClient.get('/projects', params),

  create: (data: ProjectCreateRequest): Promise<Project> =>
    apiClient.post('/projects', data),

  createWithRfp: (formData: FormData, onProgress?: (progress: number) => void): Promise<Project> =>
    apiClient.upload('/projects/create-with-rfp', formData, onProgress),
    
  createWithRfpAsync: (formData: FormData, onProgress?: (progress: number) => void): Promise<any> =>
    apiClient.upload('/projects/create-with-rfp-async', formData, onProgress),

  get: (id: string): Promise<Project> =>
    apiClient.get(`/projects/${id}`),

  update: (id: string, data: Partial<ProjectCreateRequest>): Promise<Project> =>
    apiClient.put(`/projects/${id}`, data),

  delete: (id: string): Promise<void> =>
    apiClient.delete(`/projects/${id}`),

  extractRequirements: (id: string, force_reextraction?: boolean): Promise<any> =>
    apiClient.post(`/projects/${id}/extract-requirements`, { force_reextraction }),

  getRequirements: (id: string): Promise<Requirement[]> =>
    apiClient.get(`/projects/${id}/requirements`),

  addRequirement: (id: string, requirement: Partial<Requirement>): Promise<Requirement> =>
    apiClient.post(`/projects/${id}/requirements`, requirement),

  updateRequirement: (id: string, requirementId: string, requirement: Partial<Requirement>): Promise<Requirement> =>
    apiClient.put(`/projects/${id}/requirements/${requirementId}`, requirement),

  deleteRequirement: (id: string, requirementId: string): Promise<void> =>
    apiClient.delete(`/projects/${id}/requirements/${requirementId}`),

  getStats: (): Promise<ProjectStats> =>
    apiClient.get('/projects/stats'),

  getDashboard: (id: string): Promise<any> =>
    apiClient.get(`/projects/${id}/dashboard`),

  clone: (id: string, data: { new_title: string; copy_requirements?: boolean; copy_settings?: boolean }): Promise<Project> =>
    apiClient.post(`/projects/${id}/clone`, data),

  export: (id: string, data: { format: string; include_requirements?: boolean; include_vendors?: boolean; include_evaluations?: boolean }): Promise<any> =>
    apiClient.post(`/projects/${id}/export`, data),
}

// Vendor Responses API
export interface VendorResponse {
  id: string
  project: Project
  vendor_name: string
  vendor_email?: string
  vendor_phone?: string
  vendor_address?: string
  file_path: string
  file_name: string
  file_size: number
  file_count: number
  file_names: string[]
  files?: Array<{
    file_path: string
    file_name: string
    file_size: number
    file_type: string
  }>
  page_count?: number
  total_file_size?: number
  unified_document_path?: string
  text_content: string
  submission_date: string
  notes?: string
  status: 'pending' | 'evaluated' | 'ready_for_review' | 'approved' | 'rejected'
  status_display: string
  evaluation_status: 'pending' | 'in_progress' | 'completed' | 'failed'
  overall_requirement_status: 'meets' | 'partially_meets' | 'not_met' | 'pending'
  fit_score: number
  evaluation_summary: string
  uploaded_by: User
  created_at: string
}

export interface VendorResponseCreateRequest {
  vendor_name: string
  vendor_email?: string
  vendor_phone?: string
  vendor_address?: string
  file_path: string
  submission_date?: string
  notes?: string
}

// Bulk Upload Response Types
export interface BulkUploadResponse {
  success: boolean
  message: string
  bulk_task_id: string
  vendors: Array<{
    vendor_name: string
    response_id: string
    task_id: string
    status: string
    files_count: number
  }>
  total_vendors: number
  total_files: number
  status_check_url: string
  progress_url: string
}

export interface TaskStatus {
  task_id: string
  status: 'pending' | 'in_progress' | 'completed' | 'failed'
  progress: number
  result: any
  error: string
  created_at?: string
  started_at?: string
  completed_at?: string
  execution_time?: number
}

export interface BulkUploadProgress {
  bulk_task_id: string
  total_vendors: number
  completed_vendors: number
  failed_vendors: number
  processing_vendors: number
  overall_progress: number
  vendor_details: Array<{
    vendor_name: string
    status: string
    progress: number
    task_id: string
    error?: string
  }>
  status: string
  started_at?: string
  estimated_completion?: string
}

export const vendorsApi = {
  list: (projectId: string, params?: { skip?: number; limit?: number }): Promise<PaginatedResponse<VendorResponse>> =>
    apiClient.get(`/vendors/projects/${projectId}/responses`, params),

  create: (projectId: string, data: VendorResponseCreateRequest): Promise<VendorResponse> =>
    apiClient.post(`/vendors/projects/${projectId}/responses`, data),

  // Legacy synchronous bulk upload (deprecated)
  bulkUpload: (projectId: string, formData: FormData, onProgress?: (progress: number) => void): Promise<any> =>
    apiClient.upload(`/vendors/projects/${projectId}/responses/bulk-create-with-proposals`, formData, onProgress),

  // New asynchronous bulk upload
  bulkUploadAsync: (projectId: string, formData: FormData, onProgress?: (progress: number) => void): Promise<BulkUploadResponse> =>
    apiClient.upload(`/vendors/projects/${projectId}/responses/bulk-upload-async`, formData, onProgress),

  // Progress tracking for async uploads
  getTaskStatus: (taskId: string): Promise<TaskStatus> =>
    apiClient.get(`/vendors/tasks/${taskId}/status`),

  getBulkUploadProgress: (bulkTaskId: string): Promise<BulkUploadProgress> =>
    apiClient.get(`/vendors/tasks/${bulkTaskId}/progress`),

  get: (id: string): Promise<VendorResponse> =>
    apiClient.get(`/vendors/responses/${id}`),

  update: (id: string, data: Partial<VendorResponseCreateRequest>): Promise<VendorResponse> =>
    apiClient.put(`/vendors/responses/${id}`, data),

  delete: (id: string): Promise<void> =>
    apiClient.delete(`/vendors/responses/${id}`),

  download: (id: string): Promise<void> =>
    apiClient.download(`/vendors/responses/${id}/download`),

  getText: (id: string): Promise<{ text_content: string; word_count: number; char_count: number }> =>
    apiClient.get(`/vendors/responses/${id}/text`),

  getAnalysis: (id: string): Promise<any> =>
    apiClient.get(`/vendors/responses/${id}/analysis`),

  getStats: (projectId: string): Promise<any> =>
    apiClient.get(`/vendors/projects/${projectId}/responses/stats`),
}

// Evaluations API
export interface Evaluation {
  id: string
  vendor_response: VendorResponse
  requirement_id: string
  result: 'meets' | 'partially_meets' | 'not_met'
  evidence: string
  reasoning: string
  confidence_score: number
  source_page: number
  source_section: string
  source_file_path: string | null
  text_start_index?: number
  text_length?: number
  extracted_details: any
  evaluator: User
  is_ai_generated: boolean
  created_at: string
}

export interface EvaluationCreateRequest {
  vendor_response_id: string
  requirement_id: string
  result: 'meets' | 'partially_meets' | 'not_met'
  evidence: string
  reasoning: string
  confidence_score: number
  source_page?: number
  source_section?: string
  extracted_details?: any
}

export const evaluationsApi = {
  list: (params?: { skip?: number; limit?: number }): Promise<PaginatedResponse<Evaluation>> =>
    apiClient.get('/evaluations', params),

  create: (data: EvaluationCreateRequest): Promise<Evaluation> =>
    apiClient.post('/evaluations', data),

  get: (id: string): Promise<Evaluation> =>
    apiClient.get(`/evaluations/${id}`),

  update: (id: string, data: Partial<EvaluationCreateRequest>): Promise<Evaluation> =>
    apiClient.put(`/evaluations/${id}`, data),

  delete: (id: string): Promise<void> =>
    apiClient.delete(`/evaluations/${id}`),

  evaluateResponse: (responseId: string, data: { force_reevaluation?: boolean }): Promise<any> =>
    apiClient.post(`/evaluations/responses/${responseId}/evaluate`, data),

  batchEvaluate: (data: { vendor_response_ids: string[]; force_reevaluation?: boolean }): Promise<any> =>
    apiClient.post('/evaluations/batch-evaluate', data),

  getResponseEvaluations: (responseId: string): Promise<Evaluation[]> =>
    apiClient.get(`/evaluations/responses/${responseId}/evaluations`),

  getResponseSummary: (responseId: string): Promise<any> =>
    apiClient.get(`/evaluations/responses/${responseId}/summary`),

  getProjectEvaluations: (projectId: string, params?: { skip?: number; limit?: number }): Promise<PaginatedResponse<Evaluation>> =>
    apiClient.get(`/evaluations/projects/${projectId}/evaluations`, params),

  getProjectSummary: (projectId: string): Promise<any> =>
    apiClient.get(`/evaluations/projects/${projectId}/summary`),

  getStats: (projectId?: string): Promise<any> =>
    apiClient.get('/evaluations/stats', { project_id: projectId }),
}

// Requirements API
export interface Requirement {
  requirement_id: string
  title: string
  description: string
  category: string[]
  priority: 'low' | 'medium' | 'high'
  section: string
  page_number?: number | null
  weight: number
  is_mandatory: boolean
  context: string
  extraction_confidence: number
  extracted_text: string
}

// Tasks API
export interface Task {
  task_id: string
  name: string
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled'
  result: any
  error: string
  progress: number
  progress_message: string
  started_at: string
  completed_at: string
  processing_time: number
  metadata: any
}

export interface TaskSubmissionRequest {
  task_name: string
  func_name: string
  args: any[]
  kwargs: any
  priority: 'low' | 'normal' | 'high' | 'urgent'
  max_retries: number
  timeout: number
}

export const tasksApi = {
  submit: (data: TaskSubmissionRequest): Promise<{ task_id: string; message: string; submitted_at: string }> =>
    apiClient.post('/tasks/submit', data),

  getStatus: (taskId: string): Promise<Task> =>
    apiClient.get(`/tasks/status/${taskId}`),

  cancel: (taskId: string): Promise<void> =>
    apiClient.post(`/tasks/cancel/${taskId}`),

  getQueue: (): Promise<Task[]> =>
    apiClient.get('/tasks/queue'),

  getRunning: (): Promise<Task[]> =>
    apiClient.get('/tasks/running'),

  getStats: (): Promise<any> =>
    apiClient.get('/tasks/statistics'),

  extractRequirements: (projectId: string, force_reextraction?: boolean): Promise<{ task_id: string; message: string; submitted_at: string }> =>
    apiClient.post(`/tasks/extract-requirements/${projectId}`, { force_reextraction }),

  evaluateResponse: (responseId: string, force_reevaluation?: boolean): Promise<{ task_id: string; message: string; submitted_at: string }> =>
    apiClient.post(`/tasks/evaluate-response/${responseId}`, { force_reevaluation }),

  batchEvaluateResponses: (responseIds: string[], force_reevaluation?: boolean): Promise<{ task_id: string; message: string; submitted_at: string }> =>
    apiClient.post('/tasks/batch-evaluate-responses', { response_ids: responseIds, force_reevaluation }),
}

// Communications API
export interface Message {
  id: string
  sender: User
  recipients: User[]
  subject: string
  body: string
  message_type: string
  priority: string
  created_at: string
  is_read: boolean
  related_project?: Project
  related_response?: VendorResponse
}

export interface MessageCreateRequest {
  recipient_usernames: string[]
  subject: string
  body: string
  message_type?: string
  related_project_id?: string
  related_response_id?: string
  priority?: string
  send_email?: boolean
}

export const communicationsApi = {
  sendMessage: (data: MessageCreateRequest): Promise<{ message: string; message_id: string; recipients_count: number; sent_at: string }> =>
    apiClient.post('/communications/messages', data),

  getMessages: (params?: { message_type?: string; unread_only?: boolean; limit?: number }): Promise<Message[]> =>
    apiClient.get('/communications/messages', params),

  markAsRead: (messageId: string): Promise<void> =>
    apiClient.post(`/communications/messages/${messageId}/read`),

  getUnreadCount: (): Promise<{ unread_count: number }> =>
    apiClient.get('/communications/messages/unread-count'),

  getActivityFeed: (params?: { limit?: number }): Promise<any[]> =>
    apiClient.get('/communications/activity-feed', params),

  sendProjectNotification: (projectId: string, data: { notification_type: string; message: string }): Promise<void> =>
    apiClient.post(`/communications/notifications/project/${projectId}`, data),

  sendResponseNotification: (responseId: string, data: { notification_type: string; message: string }): Promise<void> =>
    apiClient.post(`/communications/notifications/response/${responseId}`, data),
}

// Files API
export const filesApi = {
  upload: (formData: FormData, onProgress?: (progress: number) => void): Promise<any> =>
    apiClient.upload('/files/upload', formData, onProgress),

  // These endpoints are replaced by combined project/vendor creation endpoints
  // Use projectsApi.createWithRfp or vendorsApi.bulkUpload instead

  download: (filePath: string): Promise<void> =>
    apiClient.download(`/files/download/${filePath}`),

  getInfo: (filePath: string): Promise<any> =>
    apiClient.get(`/files/info/${filePath}`),

  delete: (filePath: string): Promise<void> =>
    apiClient.delete(`/files/${filePath}`),

  extractText: (filePath: string): Promise<{ text_content: string; word_count: number; char_count: number }> =>
    apiClient.get(`/files/extract-text/${filePath}`),

  getUsage: (): Promise<any> =>
    apiClient.get('/files/usage'),

  cleanup: (days: number): Promise<any> =>
    apiClient.post('/files/cleanup', { days }),
}

// Audit API
export interface AuditLog {
  id: string
  user: User
  action: string
  action_type: 'create' | 'read' | 'update' | 'delete'
  resource_type: string
  resource_id: string
  resource_name: string
  description: string
  ip_address: string
  user_agent: string
  status_code: number
  processing_time: number
  created_at: string
  details: any
}

export interface AuditSearchRequest {
  user_id?: string
  action?: string
  action_type?: string
  resource_type?: string
  resource_id?: string
  ip_address?: string
  date_from?: string
  date_to?: string
  status_code?: number
  min_processing_time?: number
  max_processing_time?: number
  search_query?: string
}

export const auditApi = {
  search: (data: AuditSearchRequest, params?: { skip?: number; limit?: number }): Promise<PaginatedResponse<AuditLog>> =>
    apiClient.post('/audit/search', data, params),

  get: (id: string): Promise<AuditLog> =>
    apiClient.get(`/audit/logs/${id}`),

  getStats: (params?: { date_from?: string; date_to?: string }): Promise<any> =>
    apiClient.get('/audit/statistics', params),

  getUserActivity: (userId: string, params?: { date_from?: string; date_to?: string }): Promise<any> =>
    apiClient.get(`/audit/users/${userId}/activity`, params),

  getMyActivity: (params?: { date_from?: string; date_to?: string }): Promise<any> =>
    apiClient.get('/audit/my-activity', params),

  getSecurityEvents: (params?: { hours?: number }): Promise<any> =>
    apiClient.get('/audit/security-events', params),

  getComplianceReport: (params?: { date_from?: string; date_to?: string }): Promise<any> =>
    apiClient.get('/audit/compliance-report', params),

  export: (data: { format: string; date_from?: string; date_to?: string; filters?: any }): Promise<any> =>
    apiClient.post('/audit/export', data),
}

// Users API
export interface UserCreateRequest {
  username: string
  email: string
  full_name: string
  password: string
  roles: string[]
  is_active?: boolean
}

export const usersApi = {
  list: (params?: { skip?: number; limit?: number }): Promise<PaginatedResponse<User>> =>
    apiClient.get('/users', params),

  create: (data: UserCreateRequest): Promise<User> =>
    apiClient.post('/users', data),

  get: (id: string): Promise<User> =>
    apiClient.get(`/users/${id}`),

  update: (id: string, data: Partial<UserCreateRequest>): Promise<User> =>
    apiClient.put(`/users/${id}`, data),

  delete: (id: string): Promise<void> =>
    apiClient.delete(`/users/${id}`),

  activate: (id: string): Promise<User> =>
    apiClient.post(`/users/${id}/activate`),

  deactivate: (id: string): Promise<User> =>
    apiClient.post(`/users/${id}/deactivate`),

  resetPassword: (id: string, data: { new_password: string }): Promise<void> =>
    apiClient.post(`/users/${id}/reset-password`, data),
}

// Project Stats Interface
export interface ProjectStats {
  total_projects: number
  active_projects: number
  closed_projects: number
  draft_projects: number
  past_due_projects: number
  total_requirements: number
  total_vendors: number
  extraction_pending: number
  extraction_failed: number
}

export default api